import {
	Box,
	Button,
	HStack,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
} from '@chakra-ui/react'
import Color from 'color'
import moment from 'moment-timezone'

import { GetIcon, Icons } from '../../../Icons'
import {
	CALENDAR_RESPONSIVE_CONTAINER,
	CALENDER_QUERIES,
} from '../../Calendar/styles/responsive'
import { useSchedulerContext } from '..'
import { checkIsEarlyOffHours } from '../utils/checkOffHours'
import { TimeFormat } from '../utils/getDefaultTimeFormat'
interface Props {
	isOpen: boolean
	onClose: () => void
	onConfirm: () => void
	onFindNewSlot: () => void
	isLoading?: boolean
	timeslot: string
}

export const OffHourConfirmationModal = ({
	isOpen,
	onClose,
	onConfirm,
	onFindNewSlot,
	timeslot,
	isLoading,
}: Props) => {
	const { styles, timezone, timeFormat } = useSchedulerContext()

	const { header, body } = getContentText({
		timeFormat,
		timeslot,
		timezone,
	})

	const text = Color(styles.text_primary)

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="sm" isCentered>
			<Box
				sx={{
					containerType: 'inline-size',
					containerName: CALENDAR_RESPONSIVE_CONTAINER,
				}}
			>
				<ModalOverlay backdropFilter="blur(4px)" />
				<ModalContent
					minW="550px"
					maxW="550px"
					borderRadius="12px"
					bg={styles.bg_primary}
					border="1px solid"
					borderColor={styles.border_primary}
					sx={{
						[CALENDER_QUERIES.xs]: {
							minW: '320px',
							maxW: '95vw',
						},
						[CALENDER_QUERIES.xxs]: {
							minW: '280px',
							maxW: '95vw',
						},
					}}
				>
					<ModalHeader
						p="20px"
						h="fit-content"
						borderRadius="12px 12px 0 0"
						border="none"
					>
						<HStack w="full" gap={2}>
							<GetIcon
								icon={Icons.alert}
								fontSize="24px"
								color={text.fade(0.45).toString()}
								sx={{
									[CALENDER_QUERIES.xs]: {
										fontSize: '20px',
									},
								}}
							/>
							<Text
								fontSize="18px"
								fontWeight={500}
								color={styles.text_primary}
								sx={{
									[CALENDER_QUERIES.xs]: {
										fontSize: '16px',
									},
								}}
							>
								{header}
							</Text>
						</HStack>
						<ModalCloseButton color={styles.text_primary} />
					</ModalHeader>
					<ModalBody px="20px" py={0}>
						<Text fontSize="14px" fontWeight={400} color={styles.text_primary}>
							{body}
						</Text>
					</ModalBody>
					<ModalFooter
						p="20px"
						mt={2}
						gap={3}
						borderRadius="0 0 12px 12px"
						border="none"
					>
						<Button
							size="sm"
							bg={styles.bg_secondary}
							border="1px solid"
							borderColor={styles.border_primary}
							color={styles.text_primary}
							rounded="6px"
							fontWeight={500}
							fontSize="14px"
							_hover={{
								bg: styles.bg_secondary,
							}}
							onClick={onConfirm}
							isLoading={isLoading}
						>
							Keep current slot
						</Button>
						<Button
							size="sm"
							onClick={onFindNewSlot}
							bg={styles.btn_bg_primary}
							color={styles.text_primary}
							rounded="6px"
							fontWeight={500}
							fontSize="14px"
							_hover={{
								bg: styles.btn_bg_primary,
							}}
						>
							Find a new slot
						</Button>
					</ModalFooter>
				</ModalContent>
			</Box>
		</Modal>
	)
}

const getContentText = ({
	timeslot,
	timezone,
	timeFormat,
}: {
	timeslot: string
	timezone: string
	timeFormat: TimeFormat
}): {
	header: string
	body: string
} => {
	const isEarlyOffHours = checkIsEarlyOffHours(timeslot, timezone)

	const format = timeFormat === '24hr' ? 'HH:mm A' : 'h:mm A'

	const selectedTime = moment(timeslot).tz(timezone).format(format)

	if (isEarlyOffHours) {
		return {
			header: 'Are you an early bird?',
			body: `Looks like you’re booking for ${selectedTime} your local time (${timezone}). That’s pretty early! Want to grab a slot at a time that's more convenient?`,
		}
	}

	return {
		header: 'You must be a night owl!',
		body: `Looks like you’re booking for ${selectedTime} your local time (${timezone}). That’s pretty late! Want to grab a slot at a time that's more convenient?`,
	}
}
